import React, { useCallback, useEffect, useState } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { Box } from '@atlaskit/primitives/compiled';

import type { SpaceRole } from '@confluence/space-roles/entry-points/space-role-types.ts';
import { ViewRolePermissionModal } from '@confluence/space-roles/entry-points/Dialogs.tsx';
import {
	normalizeSpacePermissionAll,
	normalizeSpaceRoleList,
} from '@confluence/space-roles/entry-points/normalizers.ts';
import type { SpaceRoleNode } from '@confluence/space-roles/entry-points/graphql.ts';
import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';

import { useViewPermissionsModal } from '../../useViewPermissionsModal.tsx';

import { ViewPermissionsInfoButton } from './ViewPermissionsInfoButton.tsx';
import { InfoPopupContent } from './InfoPopupContent.tsx';
export const RoleSelectInfoButton = ({
	analyticsSource,
	currentRole,
	roleLookup,
	flags,
}: {
	currentRole: SpaceRole | null;
	roleLookup: SpaceRole[];
	analyticsSource: string;
	flags: FlagsStateContainer;
}) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [isPopupOpen, setIsPopupOpen] = useState(false);
	const [isViewPermissionsModalOpen, setIsViewPermissionsModalOpen] = useState(false);

	const { permissions, roles } = useViewPermissionsModal({ flags });

	const systemRoles = roles.filter((role) => role?.roleType === 'SYSTEM');
	const customRoles = roles.filter((role) => role?.roleType === 'CUSTOM');

	const onInfoButtonClick = useCallback(() => {
		setIsPopupOpen(!isPopupOpen);
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				actionSubject: 'button',
				action: 'clicked',
				actionSubjectId: 'role-select-info-button',
				source: analyticsSource,
				attributes: {
					roleId: currentRole?.id,
				},
			},
		}).fire();
	}, [isPopupOpen, currentRole?.id, analyticsSource, createAnalyticsEvent]);

	const onPopupContentCtaClick = useCallback(() => {
		setIsPopupOpen(false);
		setIsViewPermissionsModalOpen(true);
	}, []);

	const popupRef = React.useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (popupRef.current) {
			const handleClickOutside = (e: MouseEvent) => {
				if (popupRef.current && !popupRef.current.contains(e.target as Node)) {
					setIsPopupOpen(false);
				}
			};

			document.addEventListener('click', handleClickOutside);
			return () => document.removeEventListener('click', handleClickOutside);
		}
	});

	return (
		<>
			{isViewPermissionsModalOpen && (
				<ViewRolePermissionModal
					systemRoles={normalizeSpaceRoleList(
						systemRoles.filter(
							(r): r is NonNullable<typeof r> => r !== null,
						) as unknown as SpaceRoleNode[],
					)}
					customRoles={normalizeSpaceRoleList(
						customRoles.filter(
							(r): r is NonNullable<typeof r> => r !== null,
						) as unknown as SpaceRoleNode[],
					)}
					allPermissions={normalizeSpacePermissionAll(permissions)}
					initialActiveRoleId={currentRole?.id}
					analyticsSource={analyticsSource}
					onClose={() => setIsViewPermissionsModalOpen(false)}
				/>
			)}
			{currentRole ? (
				<Box ref={popupRef}>
					<ViewPermissionsInfoButton
						data-testid="role-selector-info-button"
						isOpen={isPopupOpen}
						onClick={onInfoButtonClick}
						content={
							<InfoPopupContent
								onCtaClick={onPopupContentCtaClick}
								roleId={currentRole.id}
								permissions={currentRole.permissions}
								roleLookup={roleLookup}
							/>
						}
					/>
				</Box>
			) : null}
		</>
	);
};
